"use client";
import React, { useState, useEffect, useCallback, useMemo } from "react";

import { FeatureFlags } from "@/enums/feature-flags";
import {
  getFeatureFlag,
  setFeatureFlag,
  getAllFeatureFlags,
  setMultipleFeatureFlags,
} from "@/services/cookies/feature-flags.service";

/**
 * Hook to use a single feature flag
 */
export const useFeatureFlag = (flag: FeatureFlags): boolean => {
  const [enabled, setEnabled] = useState(() => {
    // Initialize with current value from cookies
    if (typeof window !== "undefined") {
      return getFeatureFlag(flag);
    }
    return false;
  });

  useEffect(() => {
    // Update state when component mounts or flag changes
    setEnabled(getFeatureFlag(flag));
  }, [flag]);

  return enabled;
};

/**
 * Hook to use a feature flag with toggle functionality
 */
export const useFeatureFlagToggle = (
  flag: FeatureFlags,
): [boolean, (enabled: boolean) => void] => {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      return getFeatureFlag(flag);
    }
    return false;
  });

  useEffect(() => {
    setEnabled(getFeatureFlag(flag));
  }, [flag]);

  const toggle = useCallback(
    (newEnabled: boolean) => {
      setFeatureFlag(flag, newEnabled);
      setEnabled(newEnabled);
    },
    [flag],
  );

  return [enabled, toggle];
};

/**
 * Hook to manage all feature flags at once
 */
export const useAllFeatureFlags = (): [
  Record<FeatureFlags, boolean>,
  (flag: FeatureFlags, enabled: boolean) => void,
  (flags: Partial<Record<FeatureFlags, boolean>>) => void,
  () => void,
] => {
  const [flags, setFlags] = useState<Record<FeatureFlags, boolean>>(() => {
    if (typeof window !== "undefined") {
      return getAllFeatureFlags();
    }
    return {} as Record<FeatureFlags, boolean>;
  });

  useEffect(() => {
    // Refresh flags on mount
    setFlags(getAllFeatureFlags());
  }, []);

  const updateFlag = useCallback((flag: FeatureFlags, enabled: boolean) => {
    setFeatureFlag(flag, enabled);
    setFlags((prev) => ({ ...prev, [flag]: enabled }));
  }, []);

  const updateMultipleFlags = useCallback(
    (newFlags: Partial<Record<FeatureFlags, boolean>>) => {
      setMultipleFeatureFlags(newFlags);
      setFlags((prev) => ({ ...prev, ...newFlags }));
    },
    [],
  );

  const refreshFlags = useCallback(() => {
    setFlags(getAllFeatureFlags());
  }, []);

  return [flags, updateFlag, updateMultipleFlags, refreshFlags];
};

/**
 * Hook for checking multiple flags at once (useful for complex conditions)
 */
export const useMultipleFeatureFlags = (
  flagsList: FeatureFlags[],
): Record<FeatureFlags, boolean> => {
  return useMemo(() => {
    const result = {} as Record<FeatureFlags, boolean>;

    flagsList.forEach((flag) => {
      result[flag] = getFeatureFlag(flag);
    });

    return result;
  }, [flagsList.join("|")]);
};

/**
 * Hook for conditional rendering based on feature flags
 */
export const useFeatureFlagComponent = (
  flag: FeatureFlags,
): {
  isEnabled: boolean;
  FeatureComponent: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }>;
} => {
  const isEnabled = useFeatureFlag(flag);

  const FeatureComponent: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
  }> = ({ children, fallback = null }) => {
    return React.createElement(
      React.Fragment,
      {},
      isEnabled ? children : fallback,
    );
  };

  return { isEnabled, FeatureComponent };
};
